import React, { useState, useEffect } from 'react';
import { X, Bell, BellOff, ExternalLink } from 'lucide-react';
import './ArtistNotificationsModal.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get token from either key
const getAuthToken = () => {
  return localStorage.getItem('beatseek_token') || localStorage.getItem('token');
};

const ArtistNotificationsModal = ({ isOpen, onClose }) => {
  const [followedArtists, setFollowedArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchFollowedArtists();
    }
  }, [isOpen]);

  const fetchFollowedArtists = async () => {
    const token = getAuthToken();
    if (!token) {
      setError('Please log in to view followed artists');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/artists/following`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFollowedArtists(data.artists || []);
      } else {
        setError('Failed to load followed artists');
      }
    } catch (err) {
      setError('Error loading followed artists');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUnfollow = async (artistName) => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/artists/follow/${encodeURIComponent(artistName)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        setFollowedArtists(prev => prev.filter(a => a.artist_name !== artistName));
      }
    } catch (err) {
      console.error('Error unfollowing artist:', err);
    }
  };

  const handleToggleNotifications = async (artistName, currentState) => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/artists/follow/${encodeURIComponent(artistName)}/notifications`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ enabled: !currentState })
        }
      );

      if (response.ok) {
        setFollowedArtists(prev => 
          prev.map(a => 
            a.artist_name === artistName 
              ? { ...a, notifications_enabled: !currentState }
              : a
          )
        );
      }
    } catch (err) {
      console.error('Error toggling notifications:', err);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const searchArtist = (artistName) => {
    window.open(`https://www.google.com/search?q=${encodeURIComponent(artistName + ' electronic music')}`, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="artist-notifications-modal-overlay" onClick={onClose}>
      <div className="artist-notifications-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-title">
            <Bell className="header-icon" />
            <h2>Artist Notifications</h2>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading followed artists...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
            </div>
          ) : followedArtists.length === 0 ? (
            <div className="empty-state">
              <Bell size={48} className="empty-icon" />
              <h3>No Artists Followed</h3>
              <p>When you follow artists in the Discovery Zone, they'll appear here. You'll receive email notifications when they have new content on BEATSEEK!</p>
            </div>
          ) : (
            <>
              <p className="artists-count">
                Following <strong>{followedArtists.length}</strong> artist{followedArtists.length !== 1 ? 's' : ''}
              </p>
              <div className="artists-list">
                {followedArtists.map((artist) => (
                  <div key={artist.id} className="artist-card">
                    <div className="artist-info">
                      {artist.artwork_url ? (
                        <img 
                          src={artist.artwork_url} 
                          alt={artist.artist_name}
                          className="artist-artwork"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="artist-artwork-placeholder" 
                        style={{ display: artist.artwork_url ? 'none' : 'flex' }}
                      >
                        🎵
                      </div>
                      <div className="artist-details">
                        <h4 className="artist-name">{artist.artist_name}</h4>
                        <div className="artist-meta">
                          {artist.source && (
                            <span className={`source-tag source-${artist.source}`}>
                              {artist.source}
                            </span>
                          )}
                          <span className="follow-date">
                            Since {formatDate(artist.followed_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="artist-actions">
                      <button
                        className={`action-btn notification-toggle ${artist.notifications_enabled ? 'enabled' : 'disabled'}`}
                        onClick={() => handleToggleNotifications(artist.artist_name, artist.notifications_enabled)}
                        title={artist.notifications_enabled ? 'Disable notifications' : 'Enable notifications'}
                      >
                        {artist.notifications_enabled ? <Bell size={16} /> : <BellOff size={16} />}
                      </button>
                      <button
                        className="action-btn search-btn"
                        onClick={() => searchArtist(artist.artist_name)}
                        title="Search artist"
                      >
                        <ExternalLink size={16} />
                      </button>
                      <button
                        className="action-btn unfollow-btn"
                        onClick={() => handleUnfollow(artist.artist_name)}
                        title="Unfollow artist"
                        style={{ fontSize: '14px' }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <p className="footer-note">
            📧 You'll receive email notifications when followed artists have new content on BEATSEEK
          </p>
        </div>
      </div>
    </div>
  );
};

export default ArtistNotificationsModal;
